import { useEffect, useState } from "react";
import { Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";
import { getNbaBestBets } from "../../lib/api";

const MARKET_LABEL = { MONEYLINE: "ML", SPREAD: "Spread", TOTAL: "Total" };

const leanPickText = (lean) => {
  if (lean.market_type === "MONEYLINE") return `${lean.team} ML`;
  if (lean.market_type === "SPREAD") return `${lean.team} ${lean.line > 0 ? "+" : ""}${lean.line}`;
  return `${lean.side === "OVER" ? "Over" : "Under"} ${lean.line}`;
};

const RESULT_DOT = { WIN: "bg-green-500", LOSS: "bg-red-500", PUSH: "bg-zinc-500" };

// Fetches once, renders both "Largest Leans" (pending, forward-only qualified picks) and
// "Recent Results" (graded) -- same user-facing headings as baseball's panels, but backed by a
// REAL forward-tracked collection (nba_leans, see services/prediction/nba_leans_tracker.py),
// not a client-side confidence filter.
export const NbaLeansAndResultsPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getNbaBestBets()
      .then(setData)
      .catch(() => setError("Couldn't load NBA Largest Leans."));
  }, []);

  return (
    <>
      <div data-testid="nba-panel-largest-leans" className="rounded-lg border border-white/10 bg-eb-surface p-4">
        <h3 className="mb-3 font-heading text-lg uppercase tracking-tight text-white">Largest Leans</h3>
        {error ? (
          <p className="font-mono text-xs text-zinc-500">{error}</p>
        ) : !data ? (
          <div data-testid="nba-leans-loading" className="py-6 text-center font-mono text-xs text-zinc-500">Scanning today&apos;s slate&hellip;</div>
        ) : data.largest_leans.length > 0 ? (
          <div data-testid="nba-leans-list" className="flex flex-col gap-2">
            {data.largest_leans.map((lean) => (
              <div
                key={lean.id}
                data-testid={`nba-lean-${lean.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-white/5 bg-eb-bg px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">
                    {MARKET_LABEL[lean.market_type]} &middot; {lean.away_team} @ {lean.home_team}
                  </span>
                  <span className="font-heading text-sm text-white">{leanPickText(lean)}</span>
                </div>
                <span data-testid={`nba-lean-${lean.id}-edge`} className="shrink-0 font-mono text-sm text-white">
                  {(lean.model_probability * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Sparkle}
            title="No Strong Leans Yet"
            description="Largest Leans appear once a game's model probability tops 60% AND beats the bookmaker's own no-vig price -- requires real matched odds."
            testId="nba-empty-state-leans"
          />
        )}
      </div>

      <div data-testid="nba-panel-recent-results" className="rounded-lg border border-white/10 bg-eb-surface p-4">
        <h3 className="mb-3 font-heading text-lg uppercase tracking-tight text-white">Recent Results</h3>
        {data && data.summary.win_pct != null && (
          <p data-testid="nba-results-record" className="mb-2 font-mono text-[11px] text-zinc-500">
            {data.summary.wins}-{data.summary.losses}-{data.summary.pushes} ({data.summary.win_pct}%)
          </p>
        )}
        {!data ? null : data.recent_results.length > 0 ? (
          <div data-testid="nba-results-list" className="flex flex-col gap-2">
            {data.recent_results.map((lean) => (
              <div key={lean.id} data-testid={`nba-result-${lean.id}`} className="flex items-center justify-between gap-3 rounded-md border border-white/5 bg-eb-bg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${RESULT_DOT[lean.result] || "bg-zinc-500"}`} />
                  <div className="flex flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">
                      {MARKET_LABEL[lean.market_type]} &middot; {lean.away_team} @ {lean.home_team}
                    </span>
                    <span className="font-heading text-sm text-white">{leanPickText(lean)}</span>
                  </div>
                </div>
                <span data-testid={`nba-result-${lean.id}-status`} className="shrink-0 font-mono text-xs uppercase text-zinc-400">{lean.result}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={ClockCounterClockwise}
            title="No Graded Leans Yet"
            description="Settled Largest Leans will appear here once their games finish and the real final score has synced."
            testId="nba-empty-state-results"
          />
        )}
      </div>
    </>
  );
};
